import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import emailIcon from "@iconify/icons-mdi/email";
import { InlineIcon } from "@iconify/react";
import { useMyHrPermissionForm } from "my-hr/hooks";
import { OcDatePicker } from "oc/components";
import { getDateFormatByLocale } from "oc/utils";

interface MyHrAddPermissionFormProps {
  user: {
    email?: string;
    validFrom?: Date | string | null;
    validTo?: Date | string | null;
    [key: string]: any;
  };
  employee: {
    emails?: { type: string; address: string }[];
    [key: string]: any;
  };
  setUser: (user: any) => void;
  dateError?: boolean;
  isMobile?: boolean;
}

export const MyHrAddPermissionForm = ({
  user,
  employee,
  setUser,
  dateError,
  isMobile,
}: MyHrAddPermissionFormProps) => {
  const {
    email,
    onChangeEmail,
    onChangeValidFrom,
    onClearError,
    validFrom,
    validTo,
    onChangeValidTo,
  } = useMyHrPermissionForm();

  const { t, i18n } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    if (email?.value !== user?.email) {
      setUser({ email: email.value });
    }
  }, [email?.value, user?.email, setUser]);

  useEffect(() => {
    if (validFrom?.value !== user?.validFrom) {
      setUser({ validFrom: validFrom?.value });
    }
  }, [validFrom?.value, user?.validFrom, setUser]);

  useEffect(() => {
    if (validTo?.value !== user?.validTo) {
      setUser({ validTo: validTo?.value });
    }
  }, [validTo?.value, user?.validTo, setUser]);

  const [goBack, setGoBack] = React.useState(true);

  const workEmail = employee?.emails?.find((item) => item.type === "work");

  useEffect(() => {
    if (email.value === "" && workEmail?.address && goBack) {
      onChangeEmail(workEmail.address);
      setGoBack(false);
    }
  }, [email.value, workEmail?.address, goBack, onChangeEmail]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box width={isMobile ? "100%" : "432px"} paddingTop="12px">
        <form noValidate autoComplete="off">
          <Box marginBottom="36px">
            <TextField
              id="outlined-basic"
              label={t("MY_HR_EMAIL")}
              variant="outlined"
              value={email?.value || ""}
              onChange={(e) => onChangeEmail(e.target.value)}
              fullWidth
              error={email.error}
              helperText={email.error ? email.errorText : ""}
              onFocus={onClearError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InlineIcon
                      height="24px"
                      icon={emailIcon}
                      color={theme.palette.gray60.main}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box marginBottom="36px">
            <OcDatePicker
              error={validFrom.error}
              helperText={validFrom.error && t("OC_NOT_SELECTABLE")}
              id="validFrom"
              label={t("OC_VALID_FROM")}
              minDate={new Date()}
              onChange={(date) => onChangeValidFrom(date ?? "")}
              value={validFrom.value || null}
              format={getDateFormatByLocale(i18n.language)}
            />
          </Box>
          <Box marginBottom="12px">
            <OcDatePicker
              error={validTo.error}
              helperText={
                validTo.error ? t("OC_NOT_SELECTABLE") : t("OC_OPTIONAL")
              }
              id="validTo"
              label={t("OC_VALID_TO")}
              minDate={validFrom.value}
              onChange={(date) => onChangeValidTo(date ?? "")}
              format={getDateFormatByLocale(i18n.language)}
              value={validTo.value || null}
            />
          </Box>
          <Box height="20px" />
          {dateError && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              paddingTop="24px"
              paddingBottom="12px"
            >
              <Typography variant="body1" color="primary">
                {t("MY_HR_DATE_RANGE_ERROR")}
              </Typography>
            </Box>
          )}
        </form>
      </Box>
    </Box>
  );
};

export default MyHrAddPermissionForm;
